// src/components/Footer.jsx
import { Link } from "react-router-dom";
import {
  Facebook,
  Instagram,
  Send,
  Youtube,
  Play,
  Users,
  TrendingUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const Footer = () => {
  const navigate = useNavigate();

  // Social media data with colors and follower counts
  const socialLinks = [
    {
      icon: Youtube,
      href: "https://www.youtube.com/@SYMMachinery",
      label: "YouTube",
      color: "bg-red-600",
      type: "video",
    },
    {
      icon: Facebook,
      href: "/facebook-updates",
      label: "Facebook",
      color: "bg-blue-600",
      type: "social",
    },
    {
      icon: Instagram,
      href: "/instagram-updates",
      label: "Instagram",
      color: "bg-pink-600",
      type: "social",
    },
  ];

  // YouTube specific links
  const youtubeLinks = [
    "Watch Our Latest Videos",
    "Forklift Reviews",
    "Maintenance Tips",
    "Product Demos",
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
        {/* Mobile: Single column layout */}
        <div className="lg:hidden flex flex-col gap-10">
          {/* Logo & Description Section */}
          <div className="space-y-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <img
                src="https://symforklift.nyc3.cdn.digitaloceanspaces.com/logo.webp"
                alt="SYM Forklifts Logo"
                className="h-40 w-auto"
              />
              <div className="bg-gradient-to-r px-6 py-3 rounded-lg">
                <span className="text-white font-bold text-4xl tracking-tight">
                  SYM <span className="text-orange-400">FORKLIFTS</span>
                </span>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed text-xl text-center px-4">
              Founded on a legacy of engineering excellence, SYM Forklifts is a
              family-owned business committed to enhancing the forklift industry
              in Australia.
            </p>

            {/* ===== ENHANCED SOCIAL SECTION - MOBILE ===== */}
            <div className="w-full bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl p-6 border border-yellow-500/20">
              {/* Attractive heading above social buttons */}
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Users className="w-6 h-6 text-yellow-500" />
                  <span className="text-yellow-400 font-semibold text-sm uppercase tracking-wider">
                    Join Our Community
                  </span>
                </div>
                <h3 className="text-white font-bold text-2xl">
                  Follow Us For Updates &amp; Videos
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  Stay connected with us on social media
                </p>
              </div>

              {/* YouTube Featured Section */}
              <div className="bg-gradient-to-r from-red-600/20 to-red-500/10 p-4 rounded-xl border border-red-500/30 mb-6">
                {/* Quick YouTube Links */}
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {youtubeLinks.map((link, idx) => (
                    <a
                      key={idx}
                      href="https://www.youtube.com/@SYMMachinery"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-gray-300 hover:text-yellow-400 flex items-center gap-1 bg-black/30 px-2 py-1 rounded"
                    >
                      <Play className="w-3 h-3 text-red-500" />
                      {link}
                    </a>
                  ))}
                </div>
              </div>

              {/* Social Icons with Followers */}
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {socialLinks.map(
                  ({ icon: Icon, href, label, color, followers }, index) => (
                    <div
                      key={index}
                      className="flex flex-col items-center group"
                    >
                      <a
                        href={href}
                        target={href.startsWith("/") ? "_self" : "_blank"}
                        rel={href.startsWith("/") ? undefined : "noopener noreferrer"}
                        className={`relative w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center ${color} transition-all duration-300 shadow-lg hover:scale-110 hover:shadow-2xl hover:shadow-yellow-500/20 group`}
                        aria-label={`Follow us on ${label}`}
                      >
                        {/* Glow effect */}
                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-sm"></div>
                        <Icon className="w-8 h-8 text-white group-hover:text-white transition-all duration-300 relative z-10" />
                        {label === "YouTube" && (
                          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[8px] px-1.5 py-0.5 rounded-full animate-pulse font-bold">
                            SUB
                          </span>
                        )}
                      </a>
                      <span className="text-xs font-semibold text-yellow-500 mt-1">
                        {followers}
                      </span>
                      <span className="text-xs text-gray-400">{label}</span>
                    </div>
                  ),
                )}
              </div>

              {/* Follow Button */}
              <div className="mt-4 text-center">
                <a
                  href="https://www.youtube.com/@SYMMachinery"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold rounded-full hover:scale-105 transition-all duration-300 shadow-lg shadow-yellow-500/30 text-sm uppercase tracking-wider"
                >
                  <Youtube className="w-5 h-5" />
                  Subscribe Now
                  <TrendingUp className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="flex justify-center lg:justify-start">
              <button
                onClick={() => navigate("/become-a-dealer")}
                className="min-w-[120px] sm:min-w-[140px] md:min-w-[160px]
               px-4 md:px-6 lg:px-8 py-1.5 sm:py-2 md:py-2.5
               bg-[#FFA203] hover:bg-[#e08f00]
               text-black font-semibold rounded-md sm:rounded-lg
               shadow-lg flex items-center justify-center gap-1.5
               text-[20px] md:text-lg"
              >
                Become a Dealer
              </button>
            </div>
          </div>

          {/* Mobile: Single row for Quick Links & Our Range */}
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-8 min-w-max px-4">
              {/* Quick Links */}
              <div className="min-w-[180px]">
                <h4 className="text-white font-bold mb-6 text-2xl md:text-5xl pb-3 border-b border-yellow-500/30">
                  Quick Links
                </h4>
                <ul className="space-y-4">
                  {[
                    { name: "Home", path: "/" },
                    { name: "About Us", path: "/about" },
                    { name: "Finance", path: "/finance" },
                    { name: "Videos", path: "/videos" },
                    { name: "Instagram Updates", path: "/instagram-updates" },
                  { name: "Facebook Updates", path: "/facebook-updates" },
                    { name: "Contact", path: "/contact" },
                  ].map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.path}
                        className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 text-xl md:text-4xl hover:pl-3 block py-2"
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Our Range */}
              <div className="min-w-[200px]">
                <h4 className="text-white font-bold mb-6 text-2xl md:text-5xl pb-3 border-b border-yellow-500/30">
                  Our Range
                </h4>
                <ul className="space-y-4">
                  {[
                    {
                      name: "All Machines",
                      href: "/machines",
                    },
                    {
                      name: "Excavators",
                      href: "/machines?category=692fd8b8e4e7ea23f73c25af",
                    },
                    {
                      name: "Site Dumpers",
                      href: "/machines?category=69f7d854f54525839194f0b4",
                    },
                    {
                      name: "Diesel Forklifts",
                      href: "/machines?category=6933f3af4edee27b3687607b",
                    },
                    {
                      name: "Rough Terrain Forklifts",
                      href: "/machines?category=692fd8d5e4e7ea23f73c25bb",
                    },
                  ].map((item) => (
                    <li key={item.name}>
                      <a
                        href={item.href}
                        className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 text-xl md:text-4xl hover:pl-3 block py-2"
                      >
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Newsletter - Mobile */}
          <div className="px-4">
            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-yellow-500/20 rounded-2xl p-8">
              <h4 className="text-white font-bold mb-4 text-2xl md:text-5xl">
                Stay Updated With Our Newsletter
              </h4>
              <p className="text-gray-300 text-xl md:text-3xl mb-8">
                Get latest updates and Offers.
              </p>

              <div className="relative mb-6">
                <input
                  type="email"
                  placeholder="email@address.com"
                  className="w-full px-6 py-5 bg-black/50 border border-gray-700 text-white text-xl md:text-3xl rounded-xl focus:outline-none focus:border-yellow-500 placeholder:text-gray-500"
                />
              </div>

              <button
                type="submit"
                className="w-full py-5 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold rounded-xl hover:from-yellow-600 hover:to-amber-600 transition-all duration-300 shadow-lg shadow-yellow-500/25 uppercase tracking-wider text-xl md:text-3xl flex items-center justify-center gap-3"
              >
                <Send className="w-6 h-6 md:w-10 md:h-10" />
                SUBSCRIBE NOW
              </button>

              <div className="mt-10 pt-6 border-t border-gray-800 text-center">
                <p className="text-yellow-500 text-lg">
                  Copyright {new Date().getFullYear()} © All Rights Reserved SYM
                  FORKLIFTS
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ===== DESKTOP VERSION ===== */}
        <div className="hidden lg:grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Column 1 - Logo & Description */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <img
                src="https://symforklift.nyc3.cdn.digitaloceanspaces.com/logo.webp"
                alt="SYM Forklifts Logo"
                className="lg:h-24 md:h-44 w-auto"
              />
              <div className="bg-gradient-to-r px-4 py-2 rounded-lg">
                <span className="text-white font-bold lg:text-2xl md:text-6xl tracking-tight">
                  SYM <span className="text-orange-400">FORKLIFTS</span>
                </span>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed lg:text-sm md:text-xl">
              Founded on a legacy of engineering excellence, SYM Forklifts is a
              family-owned business committed to enhancing the forklift industry
              in Australia.
            </p>

            {/* ===== ENHANCED SOCIAL SECTION - DESKTOP ===== */}
            <div className=" rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-yellow-500" />
                <span className="text-yellow-400 font-semibold text-xs uppercase tracking-wider">
                  Connect With Us
                </span>
              </div>
              {/* Social Icons - Desktop */}
              <div className="flex flex-wrap items-center gap-3">
                {socialLinks.map(
                  ({ icon: Icon, href, label, color, followers }, index) => (
                    <div
                      key={index}
                      className="flex flex-col items-center group"
                    >
                      <a
                        href={href}
                        target={href.startsWith("/") ? "_self" : "_blank"}
                        rel={href.startsWith("/") ? undefined : "noopener noreferrer"}
                        style={{
                          backgroundColor:
                            label === "Facebook"
                              ? "#1877F2" // Facebook Blue
                              : label === "Instagram"
                                ? "#E4405F" // Instagram Pink
                                : label === "YouTube"
                                  ? "#FF0000" // YouTube Red
                                  : undefined,
                        }}
                        className="relative w-12 h-12 rounded-full flex items-center justify-center"
                        aria-label={`Follow us on ${label}`}
                      >
                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-sm"></div>
                        <Icon className="w-5 h-5 text-white group-hover:text-white transition-all duration-300 relative z-10" />
                      </a>
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>

          {/* Column 2 - Quick Links & Our Range */}
          <div className="grid grid-cols-2 gap-8">
            <div>
              <ul className="space-y-3">
                {[
                  { name: "Home", path: "/" },
                  { name: "About Us", path: "/about" },
                  { name: "Finance", path: "/finance" },
                  { name: "Videos", path: "/videos" },
                  { name: "Instagram Updates", path: "/instagram-updates" },
                  { name: "Facebook Updates", path: "/facebook-updates" },
                  { name: "Contact Us", path: "/contact" },
                  { name: "Spare Parts", path: "/spareparts" },
                ].map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.path}
                      className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 text-sm hover:pl-2 block py-1"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4 text-lg pb-2 border-b border-yellow-500/30">
                Our Range
              </h4>
              <ul className="space-y-4">
                {[
                  {
                    name: "All Machines",
                    href: "/machines",
                  },
                  {
                    name: "Excavators",
                    href: "/machines?category=692fd8b8e4e7ea23f73c25af",
                  },
                  {
                    name: "Site Dumpers",
                    href: "/machines?category=69f7d854f54525839194f0b4",
                  },
                  {
                    name: "Diesel Forklifts",
                    href: "/machines?category=6933f3af4edee27b3687607b",
                  },
                  {
                    name: "Rough Terrain Forklifts",
                    href: "/machines?category=692fd8d5e4e7ea23f73c25bb",
                  },
                ].map((item) => (
                  <li key={item.name}>
                    <a
                      href={item.href}
                      className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 text-sm hover:pl-2 block py-1"
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Column 3 - Newsletter */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-yellow-500/20 rounded-2xl p-6">
              <h4 className="text-white font-bold mb-2 text-lg">
                Stay Updated With Our Newsletter
              </h4>
              <p className="text-gray-300 text-sm mb-6">
                Get latest updates and Offers.
              </p>

              <div className="relative mb-4">
                <input
                  type="email"
                  placeholder="email@address.com"
                  className="w-full px-4 py-3 bg-black/50 border border-gray-700 text-white rounded-lg focus:outline-none focus:border-yellow-500 text-sm placeholder:text-gray-500"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold rounded-lg hover:from-yellow-600 hover:to-amber-600 transition-all duration-300 shadow-lg shadow-yellow-500/25 uppercase tracking-wider text-sm flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                SUBSCRIBE NOW
              </button>

              <button
                type="submit"
                className="w-full py-3 pt-4 mt-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold rounded-lg hover:from-yellow-600 hover:to-amber-600 transition-all duration-300 shadow-lg shadow-yellow-500/25 uppercase tracking-wider text-sm flex items-center justify-center gap-2"
              >
                <button onClick={() => navigate("/become-a-dealer")}>
                  Become a Dealer
                </button>
              </button>

              <div className="mt-6 pt-6 border-t border-gray-800 lg:hidden text-center">
                <p className="text-yellow-500 text-xs">
                  Copyright {new Date().getFullYear()} © All Rights Reserved SYM
                  FORKLIFTS
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar - Desktop Only */}
        <div className="mt-12 pt-8 border-t border-gray-800 hidden lg:block">
          <div className="md:flex-row justify-between items-center gap-4">
            <div className="text-center">
              <p className="text-yellow-500 text-sm">
                Copyright {new Date().getFullYear()} © All Rights Reserved{" "}
                <span className="text-yellow-500 font-semibold">
                  SYM FORKLIFTS
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Bottom Border */}
      <div className="h-1 bg-gradient-to-r from-transparent via-yellow-500/50 to-transparent" />
    </footer>
  );
};

export default Footer;